import { useCallback, useState } from "react";
import { useAppConfig, useCampground } from "@/contexts/AppConfigContext";
import { api } from "@/services/api";
import type { BookingDetails } from "@/types/api";
import { logError } from "@/utils/logError";

interface UseBookingDetailsReturn {
  data: BookingDetails | null;
  loading: boolean;
  fetchBookingDetails: (bookingId: string) => Promise<BookingDetails | null>;
}

export function useBookingDetails(): UseBookingDetailsReturn {
  const { data: campgroundData } = useCampground();
  const { campgroundSlug } = useAppConfig();

  const [data, setData] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchBookingDetails = useCallback(
    async (bookingId: string): Promise<BookingDetails | null> => {
      const url = campgroundData.urls.booking_details;

      if (!url || !campgroundSlug || !bookingId) {
        return null;
      }

      setLoading(true);

      try {
        const response = await api.getBookingDetails(url, campgroundSlug, bookingId);
        setData(response);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setData(null);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [campgroundData.urls.booking_details, campgroundSlug],
  );

  return {
    data,
    loading,
    fetchBookingDetails,
  };
}
